import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import type { MediaAssetSlim } from "@/lib/media";
import type { MusicAttachment } from "@/lib/music/attachment";
import { user } from "./auth";

export const CHAT_TYPE = [
  "hiive",
  "event",
  "event_occurrence",
  "direct",
  /** Private conversation between one person and the shared Hiive bot. */
  "assistant",
  "group",
  "activity_session",
  /**
   * An exclusive room for one or more membership tiers.
   *
   * `entityId` is deliberately NULL: `chat_type_entity_unique` is a unique
   * index on (type, entityId), so keying these on the hiive id would cap a
   * hiive at exactly one of them — the same constraint that makes "hiive" the
   * single General chat. SQLite treats NULLs as distinct in a unique index, so
   * NULL is how the existing multi-instance types (direct, group) already
   * live here. Membership is resolved through `hiive_tier_chat_access`.
   */
  "hiive_tier",
] as const;
export const CHAT_STATUS = ["active", "archived", "deleted"] as const;

export type ChatType = (typeof CHAT_TYPE)[number];
export type ChatStatus = (typeof CHAT_STATUS)[number];
export const MESSAGE_ATTACHMENT_KIND = ["image", "video", "voice"] as const;
export type MessageAttachmentKind = (typeof MESSAGE_ATTACHMENT_KIND)[number];

/**
 * Message subtype. `type` ("user" | "system") stays untouched — it's
 * load-bearing across renderers and previews. `kind` discriminates rich user
 * messages; non-text kinds always also write a plaintext fallback into
 * `content` so previews, push bodies, and older clients degrade gracefully.
 */
export const MESSAGE_KIND = [
  "text",
  "share",
  "poll",
  "gif",
  "sticker",
  "location",
  // A song (Apple Music snapshot) — playable card. Fallback text "🎵 Song".
  "song",
] as const;
export type MessageKind = (typeof MESSAGE_KIND)[number];

export type MessageLinkPreview = {
  url: string;
  title?: string | null;
  description?: string | null;
  image?: string | null;
  siteName?: string | null;
};

/** First media of a shared entity, resolved at send time so the chat card can
 * render it at the correct aspect ratio (and autoplay video with a mute
 * toggle) instead of a fixed 16:9 still. */
export type MessageShareMedia = {
  url: string;
  kind: "image" | "video";
  posterUrl?: string | null;
  /** Intrinsic pixel dimensions, for aspect-ratio reservation. */
  width?: number | null;
  height?: number | null;
  /** Base64 ThumbHash, painted until the image or poster has loaded. */
  blurHash?: string | null;
};

/** Server-resolved snapshot of a shared entity — resilient to later deletion.
 * Media details are optional because compact entities can use `imageUrl`. */
export type MessageShareSnapshot = {
  entityType:
    | "post"
    | "event"
    | "venue"
    | "hiive"
    | "profile"
    | "activity_post"
    | "activity_session"
    | "movement_activity"
    | "movement_route"
    | "weight_training_workout"
    | "weight_training_routine"
    | "event_booking_attendee"
    | "hiive_membership"
    | "story"
    | "promotion"
    | "shop_item";
  entityId: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  href: string;
  /** Rich first media (image or video) with intrinsic dimensions. */
  media?: MessageShareMedia | null;
  /** Author identity, for cards that lead with a byline (posts, stories). */
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  /** Story expiry as epoch ms — the card shows an explicit "expired" state
   * once this passes (JSON payloads can't hold a Date). */
  expiresAt?: number | null;
  /** The shared post carries a poll; the card shows a non-interactive poll
   * chip and taps still click out to the post. */
  hasPoll?: boolean;
  /** Round identity image beside the title: a profile photo, a hiive logo. */
  avatarUrl?: string | null;
  /** Line under an identity name: "@ziyak", "120 members". */
  handle?: string | null;
  /** Start of an event, session or booked slot as epoch ms; the card renders
   * it in the viewer's own timezone. */
  startsAt?: number | null;
  /** Where it happens (venue name, area); null when the location is hidden. */
  locationName?: string | null;
  /** Headline numbers for movement and training shares, at most three. */
  stats?: MessageShareStat[] | null;
  /** Route trace for movement shares: an SVG path `d` in a 0–100 viewBox. */
  routePath?: string | null;
  /** Movement or session kind ("run", "ride", "Yoga") for the eyebrow. */
  activityType?: string | null;
  /** Price label for products and paid sessions ("₹499"). */
  priceLabel?: string | null;
  /** What a session card needs to draw the feed's scene and facts. */
  session?: MessageShareSession | null;
};

export type MessageShareStat = { label: string; value: string };

export type MessageShareSession = {
  activityId: string;
  activityIcon?: string | null;
  sessionFormat?: string | null;
  endsAt?: number | null;
  /** "public" | "buddies" | "close_friends" | "private" */
  visibility?: string | null;
  participantCount: number;
  maxParticipants?: number | null;
  /** First few confirmed players, for the scene and the avatar row. */
  participants: {
    id: string;
    displayName: string;
    avatarUrl?: string | null;
    username?: string | null;
    team?: "team1" | "team2" | null;
  }[];
};

export type MessagePayload =
  | { linkPreview: MessageLinkPreview }
  /** kind "song": a server-resolved catalog snapshot (never client-built). */
  | { song: MusicAttachment }
  | { share: MessageShareSnapshot }
  | { pollId: number }
  | {
      provider: "klipy";
      slug: string;
      urls: { gif?: string; webp?: string; mp4?: string };
      width: number;
      height: number;
    }
  | {
      lat: number;
      lng: number;
      label?: string | null;
      address?: string | null;
      /** Radius (m) of the fix that was sent, when the device reported one. */
      accuracy?: number | null;
      mode: "static";
    }
  | {
      /** Latest position the sharer's device reported. */
      lat: number;
      lng: number;
      label?: string | null;
      address?: string | null;
      accuracy?: number | null;
      mode: "live";
      /** Epoch ms the share stops on its own. */
      liveUntil: number;
      /** Epoch ms of the latest position update. */
      updatedAt: number;
      /** Epoch ms the sharer stopped early; null while sharing. */
      endedAt?: number | null;
    };

// Main chat/channel table
export const chat = sqliteTable(
  "chat",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    // What type of chat this is
    type: text("type").notNull().$type<ChatType>(),

    // Generic reference - store the ID of whatever this chat is attached to
    // For hiive: store hiive.id
    // For event: store event.id
    // For direct/group: leave null (participants tracked in chatParticipant)
    entityId: integer("entity_id"),

    // Optional name for the chat (useful for event channels, group chats)
    name: text("name"),
    description: text("description"),
    avatarMedia: text("avatar_media", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    directPairKey: text("direct_pair_key"),
    lastMessageAt: integer("last_message_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),

    status: text("status").notNull().$type<ChatStatus>().default("active"),

    // Settings
    isReadOnly: integer("is_read_only", { mode: "boolean" })
      .notNull()
      .default(false),

    // Disappearing messages: new user messages get expiresAt = now + this.
    // Null = off. Changing it never touches existing messages.
    disappearingDurationSec: integer("disappearing_duration_sec"),

    ...timestamps,
  },
  (table) => [
    index("chat_by_entity").on(table.type, table.entityId),
    index("chat_by_type").on(table.type),
    index("chat_last_message_idx").on(table.lastMessageAt),
    uniqueIndex("chat_direct_pair_unique").on(table.directPairKey),
    uniqueIndex("chat_type_entity_unique").on(table.type, table.entityId),
  ],
);

// Track who is in each chat (especially important for direct/group chats)
export const PARTICIPANT_ROLE = ["member", "admin"] as const;

export type ParticipantRole = (typeof PARTICIPANT_ROLE)[number];

export const chatParticipant = sqliteTable(
  "chat_participant",
  {
    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // Role in this specific chat (useful for group chats)
    role: text("role").notNull().$type<ParticipantRole>().default("member"),

    // For direct chats, track when user was added
    joinedAt: integer("joined_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),

    // User can leave/be removed from chat
    leftAt: integer("left_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "chat_participant_pk",
      columns: [table.chatId, table.userId],
    }),
    index("participant_by_user").on(table.userId),
    index("participant_by_chat").on(table.chatId),
  ],
);

export const chatParticipantEpoch = sqliteTable(
  "chat_participant_epoch",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    joinedAt: integer("joined_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
    leftAt: integer("left_at", { mode: "timestamp" }),
    addedByUserId: text("added_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    removedByUserId: text("removed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (table) => [
    index("chat_participant_epoch_chat_user_idx").on(
      table.chatId,
      table.userId,
    ),
    index("chat_participant_epoch_visibility_idx").on(
      table.chatId,
      table.userId,
      table.joinedAt,
      table.leftAt,
    ),
  ],
);

export const MESSAGE_STATUS = ["sent", "edited", "deleted"] as const;
export const MESSAGE_TYPE = ["user", "system"] as const;

export type MessageStatus = (typeof MESSAGE_STATUS)[number];
export type MessageType = (typeof MESSAGE_TYPE)[number];

// Individual messages
export const message = sqliteTable(
  "message",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),

    authorId: text("author_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    /** NEW: client-provided id for idempotency / optimistic reconciliation */
    clientMessageId: text("client_message_id"),

    // Message type: user messages vs system messages
    type: text("type").notNull().$type<MessageType>().default("user"),

    // Rich message subtype + structured payload (see MessagePayload).
    kind: text("kind").notNull().$type<MessageKind>().default("text"),
    payload: text("payload", { mode: "json" }).$type<MessagePayload | null>(),

    assistantName: text("assistant_name"),
    assistantHandle: text("assistant_handle"),
    content: text("content"),

    // Media attachments (JSON array of URLs)
    mediaUrls: text("media_urls", { mode: "json" }), // ["url1", "url2", ...]

    // If this is a reply to another message
    replyToId: integer("reply_to_id"),

    // Provenance for forwarded messages (no FK cascade — display-only).
    forwardedFromMessageId: integer("forwarded_from_message_id"),

    // Disappearing messages: per-chat timer stamps this at insert; reads
    // filter expired rows, a workflow sweeper scrubs them.
    expiresAt: integer("expires_at", { mode: "timestamp" }),

    // View-once media: redacted per viewer after first open.
    viewOnce: integer("view_once", { mode: "boolean" })
      .notNull()
      .default(false),

    // Pinned messages (cap enforced atomically in the action).
    pinnedExpiresAt: integer("pinned_expires_at", { mode: "timestamp" }),
    pinnedAt: integer("pinned_at", { mode: "timestamp" }),
    pinnedByUserId: text("pinned_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    status: text("status").notNull().$type<MessageStatus>().default("sent"),

    // Track edits
    editedAt: integer("edited_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (table) => [
    index("message_by_chat").on(table.chatId),
    index("message_by_author").on(table.authorId),
    index("message_by_created").on(table.createdAt),
    index("message_chat_created_cursor_idx").on(
      table.chatId,
      table.createdAt,
      table.id,
    ),
    // Delta-sync v2: surface mutated rows (reactions, votes, view-once).
    index("message_chat_updated_idx").on(table.chatId, table.updatedAt),
    // Lazy-expiry filtering + sweeper scans.
    index("message_chat_expires_idx").on(table.chatId, table.expiresAt),
    index("message_chat_pinned_idx").on(table.chatId, table.pinnedAt),

    /** Ensure we never store duplicates for the same client id */
    uniqueIndex("uniq_message_client").on(
      table.chatId,
      table.authorId,
      table.clientMessageId,
    ),
  ],
);

export const messageAttachment = sqliteTable(
  "message_attachment",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id),
    kind: text("kind").notNull().$type<MessageAttachmentKind>(),
    asset: text("asset", { mode: "json" }).$type<MediaAssetSlim>().notNull(),
    durationMs: integer("duration_ms"),
    waveform: text("waveform", { mode: "json" }).$type<number[] | null>(),
    ...timestamps,
  },
  (table) => [index("message_attachment_message_idx").on(table.messageId)],
);

// Voice note playback receipts: one row per viewer and voice attachment.
// This lets the sender see when a voice note was actually played, not only
// when the containing message was seen.
export const messageVoicePlaybackReceipt = sqliteTable(
  "message_voice_playback_receipt",
  {
    attachmentId: integer("attachment_id")
      .notNull()
      .references(() => messageAttachment.id, { onDelete: "cascade" }),
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    playedAt: integer("played_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "message_voice_playback_receipt_pk",
      columns: [table.attachmentId, table.userId],
    }),
    index("voice_playback_receipt_message_idx").on(table.messageId),
    index("voice_playback_receipt_user_idx").on(table.userId),
  ],
);

export const chatUserState = sqliteTable(
  "chat_user_state",
  {
    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    archivedAt: integer("archived_at", { mode: "timestamp" }),
    deletedAt: integer("deleted_at", { mode: "timestamp" }),
    clearedThroughMessageId: integer("cleared_through_message_id").references(
      () => message.id,
    ),
    clearedThroughAt: integer("cleared_through_at", { mode: "timestamp" }),
    pinnedAt: integer("pinned_at", { mode: "timestamp" }),
    mutedUntil: integer("muted_until", { mode: "timestamp" }),
    lastSeenMessageId: integer("last_seen_message_id").references(
      () => message.id,
    ),
    lastSeenAt: integer("last_seen_at", { mode: "timestamp" }),
    // Delivery watermark (the grey double tick): the newest message id this
    // user's device has synced. Advanced by the app's inbox sync and by push
    // delivery acks, forward only. Message ids are monotonic, so every id at
    // or below it has reached them. Seen implies delivered, so readers treat
    // it as max(delivered, seen).
    lastDeliveredMessageId: integer("last_delivered_message_id"),
    lastDeliveredAt: integer("last_delivered_at", { mode: "timestamp" }),
    // The seen cursor the OTHER side may learn about. `lastSeen*` always
    // advances (it drives unread counts); this one only when read receipts
    // were on for both people at read time, so switching receipts back on
    // never reveals reads made while they were off (WhatsApp's rule).
    // Groups and community rooms always advance it.
    receiptSeenMessageId: integer("receipt_seen_message_id"),
    receiptSeenAt: integer("receipt_seen_at", { mode: "timestamp" }),
    // Reads made while receipts were off, as one id range (after, through].
    // The receipt cursor is a single watermark, so the first visible read
    // after a private stretch would otherwise jump it past those messages
    // and turn them "seen" after the fact. WhatsApp never sends a receipt
    // for a message read while receipts were off; neither does this. Several
    // private stretches merge into one conservative range (hiding a few
    // genuine receipts, never revealing a private read).
    receiptHiddenAfterMessageId: integer("receipt_hidden_after_message_id"),
    receiptHiddenThroughMessageId: integer("receipt_hidden_through_message_id"),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "chat_user_state_pk",
      columns: [table.chatId, table.userId],
    }),
    index("chat_user_state_user_archived_idx").on(
      table.userId,
      table.archivedAt,
    ),
    index("chat_user_state_user_pinned_idx").on(table.userId, table.pinnedAt),
    index("chat_user_state_user_deleted_idx").on(table.userId, table.deletedAt),
    index("chat_user_state_seen_idx").on(
      table.chatId,
      table.userId,
      table.lastSeenMessageId,
    ),
  ],
);

export const chatTypingPresence = sqliteTable(
  "chat_typing_presence",
  {
    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "chat_typing_presence_pk",
      columns: [table.chatId, table.userId],
    }),
    index("chat_typing_presence_expiry_idx").on(table.chatId, table.expiresAt),
  ],
);

export const chatUserPresence = sqliteTable(
  "chat_user_presence",
  {
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    lastActiveAt: integer("last_active_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "chat_user_presence_pk",
      columns: [table.userId],
    }),
    index("chat_user_presence_last_active_idx").on(table.lastActiveAt),
  ],
);

// Chat polls — one poll per message (kind "poll", payload { pollId }).
export const messagePoll = sqliteTable(
  "message_poll",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id, { onDelete: "cascade" }),
    question: text("question").notNull(),
    allowsMultiple: integer("allows_multiple", { mode: "boolean" })
      .notNull()
      .default(false),
    // Presentation-only: vote rows always identify users for integrity.
    isAnonymous: integer("is_anonymous", { mode: "boolean" })
      .notNull()
      .default(false),
    closesAt: integer("closes_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (table) => [uniqueIndex("message_poll_message_unique").on(table.messageId)],
);

export const messagePollOption = sqliteTable(
  "message_poll_option",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    pollId: integer("poll_id")
      .notNull()
      .references(() => messagePoll.id, { onDelete: "cascade" }),
    label: text("label").notNull(),
    position: integer("position").notNull().default(0),
    ...timestamps,
  },
  (table) => [index("message_poll_option_poll_idx").on(table.pollId)],
);

export const messagePollVote = sqliteTable(
  "message_poll_vote",
  {
    pollId: integer("poll_id")
      .notNull()
      .references(() => messagePoll.id, { onDelete: "cascade" }),
    optionId: integer("option_id")
      .notNull()
      .references(() => messagePollOption.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    createdAt: integer("created_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "message_poll_vote_pk",
      columns: [table.optionId, table.userId],
    }),
    index("message_poll_vote_poll_user_idx").on(table.pollId, table.userId),
  ],
);

// @user mentions — drives mention pushes (mute bypass) and "mentions of you".
export const messageMention = sqliteTable(
  "message_mention",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id, { onDelete: "cascade" }),
    // Denormalized FK pair for the mentions-of-you index; message ids are
    // immutable so this is provenance, not derived state.
    chatId: integer("chat_id")
      .notNull()
      .references(() => chat.id, { onDelete: "cascade" }),
    // Null for @everyone rows.
    mentionedUserId: text("mentioned_user_id").references(() => user.id, {
      onDelete: "cascade",
    }),
    kind: text("kind")
      .notNull()
      .$type<"user" | "everyone" | "assistant">()
      .default("user"),
    createdAt: integer("created_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    index("message_mention_user_idx").on(
      table.mentionedUserId,
      table.createdAt,
    ),
    index("message_mention_chat_idx").on(table.chatId),
    index("message_mention_message_idx").on(table.messageId),
  ],
);

// Reactions to messages
export const messageReaction = sqliteTable(
  "message_reaction",
  {
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // Emoji or reaction type
    reaction: text("reaction").notNull(),

    createdAt: integer("created_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "message_reaction_pk",
      columns: [table.messageId, table.userId, table.reaction],
    }),
    index("reaction_by_message").on(table.messageId),
  ],
);

// View-once media receipts: one row per viewer who opened the media.
// The asset is only ever returned through markViewOnceOpened; reads
// report state ("available" | "opened") derived from these rows.
export const messageViewOnceReceipt = sqliteTable(
  "message_view_once_receipt",
  {
    messageId: integer("message_id")
      .notNull()
      .references(() => message.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    viewedAt: integer("viewed_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "message_view_once_receipt_pk",
      columns: [table.messageId, table.userId],
    }),
    index("view_once_receipt_by_message").on(table.messageId),
  ],
);
